import { useTranslation } from 'react-i18next';
import { ASSISTANT_PANEL_SHOWS_PREFERENCE_TOGGLES } from '@/constants/assistant';
import { Icon, type IconName } from '@/components/common/Icon';
import {
  useAssistantUserPreferencesQuery,
  useUpdateAssistantUserPreferencesMutation,
} from '@/hooks/useAssistantProactive';
import { useIsVoiceAssistantAvailable } from '@/hooks/useIsVoiceAssistantAvailable';
import { cn } from '@/lib/utils';
import { useAssistantPanel } from './assistantPanelContext';

interface HeaderButtonProps {
  iconName: IconName;
  label: string;
  isActive?: boolean;
  isToggle?: boolean;
  isStrongWhenActive?: boolean;
  onClick: () => void;
}

function HeaderButton({
  iconName,
  label,
  isActive = false,
  isToggle = false,
  isStrongWhenActive = false,
  onClick,
}: HeaderButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={isToggle ? isActive : undefined}
      className={cn(
        'w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
        isActive && isStrongWhenActive
          ? 'bg-zooprimary text-zoowhite'
          : isActive
            ? 'bg-zoovpalelavender text-zooprimary'
            : 'text-zoomuted hover:bg-zoovpalelavender hover:text-zooindigo'
      )}
    >
      <Icon name={iconName} />
    </button>
  );
}

interface AssistantPanelHeaderProps {
  onNewChat: () => void;
}

export function AssistantPanelHeader({ onNewChat }: AssistantPanelHeaderProps) {
  const { t } = useTranslation('assistant');
  const { view, setView, close, isAutoNavigateEnabled, setAutoNavigateEnabled } =
    useAssistantPanel();
  const preferencesQuery = useAssistantUserPreferencesQuery();
  const updatePreferences = useUpdateAssistantUserPreferencesMutation();
  const preferences = preferencesQuery.data;
  const canTalk = useIsVoiceAssistantAvailable();
  const showsProactiveToggle = preferences !== undefined && preferences.workspaceProactiveEnabled;
  return (
    <div className="px-4 h-14 border-b border-zooborder flex items-center gap-1 shrink-0">
      <div className="w-8 h-8 rounded-lg bg-zooprimary text-zoowhite flex items-center justify-center shadow-sm mr-1">
        <Icon name="Sparkles" />
      </div>
      <h2 className="flex-1 min-w-0 text-sm font-bold text-zooindigo">{t('panel.title')}</h2>
      {canTalk ? (
        <HeaderButton
          iconName="Mic"
          label={view === 'voice' ? t('panel.endVoice') : t('panel.talk')}
          isActive={view === 'voice'}
          isToggle
          isStrongWhenActive
          onClick={function toggleVoice() {
            setView(view === 'voice' ? 'chat' : 'voice');
          }}
        />
      ) : null}
      {ASSISTANT_PANEL_SHOWS_PREFERENCE_TOGGLES ? (
        <HeaderButton
          iconName="Navigation"
          label={t('panel.autoNavigate')}
          isActive={isAutoNavigateEnabled}
          isToggle
          onClick={function toggleAutoNavigate() {
            setAutoNavigateEnabled(isAutoNavigateEnabled === false);
          }}
        />
      ) : null}
      {ASSISTANT_PANEL_SHOWS_PREFERENCE_TOGGLES && showsProactiveToggle ? (
        <HeaderButton
          iconName="Bell"
          label={t('panel.badgeAndTips')}
          isActive={preferences.proactiveEnabled}
          isToggle
          onClick={function toggleBadgeAndTips() {
            updatePreferences.mutate({ proactiveEnabled: preferences.proactiveEnabled === false });
          }}
        />
      ) : null}
      <HeaderButton iconName="SquarePen" label={t('panel.newChat')} onClick={onNewChat} />
      <HeaderButton
        iconName="History"
        label={t('panel.history')}
        isActive={view === 'history'}
        isToggle
        onClick={function toggleHistory() {
          setView(view === 'history' ? 'chat' : 'history');
        }}
      />
      <HeaderButton iconName="X" label={t('panel.close')} onClick={close} />
    </div>
  );
}
